import {
    type MutableRefObject,
    type UIEvent,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';

import type { ReportWidgetColumnConfig } from '../types';

/**
 * Manages the sticky overlay header used by `ReportWidgetTable`.
 *
 * Why this exists:
 * - `ReportWidgetTable` needs `overflowX: auto` for short screens / tables.
 * - That horizontal overflow container complicates "normal" sticky header behavior.
 * - To preserve existing column sizing, the real table header stays in place as the
 *   browser's layout source of truth, while a duplicated sticky header is overlaid.
 *
 * What this hook handles:
 * - Measuring original header widths/heights.
 * - Keeping the overlay header dimensions in sync on data/layout changes.
 * - Mirroring horizontal scroll offset onto the overlay header.
 *
 * This is intentionally specific to `ReportWidgetTable`.
 */
interface UseReportWidgetStickyHeaderParams {
    columns: ReportWidgetColumnConfig[];
    itemsLength: number;
    tableMinWidth: number;
    scrollContainerRef: MutableRefObject<HTMLDivElement | null>;
    onTableScrolled: () => void;
}

interface UseReportWidgetStickyHeaderResult {
    bodyTableRef: MutableRefObject<HTMLTableElement | null>;
    stickyHeaderInnerRef: MutableRefObject<HTMLDivElement | null>;
    headerColumnWidths: number[];
    headerTableWidth: number | null;
    headerHeight: number;
    handleTableScroll: (event: UIEvent<HTMLDivElement>) => void;
}

function getColumnFallbackMinWidth(col: ReportWidgetColumnConfig): number {
    if (col.format === 'currency') return 112;
    if (col.format === 'number') return 84;
    if (col.format === 'percent') return 84;
    return 96;
}

export const useReportWidgetStickyHeader = ({
    columns,
    itemsLength,
    tableMinWidth,
    scrollContainerRef,
    onTableScrolled,
}: UseReportWidgetStickyHeaderParams): UseReportWidgetStickyHeaderResult => {
    const [headerColumnWidths, setHeaderColumnWidths] = useState<number[]>([]);
    const [headerTableWidth, setHeaderTableWidth] = useState<number | null>(
        null,
    );
    const [headerHeight, setHeaderHeight] = useState<number>(0);
    const bodyTableRef = useRef<HTMLTableElement | null>(null);
    const stickyHeaderInnerRef = useRef<HTMLDivElement | null>(null);

    const applyHeaderOffset = useCallback((scrollLeft: number) => {
        if (stickyHeaderInnerRef.current) {
            stickyHeaderInnerRef.current.style.transform = `translateX(-${scrollLeft}px)`;
        }
    }, []);

    const syncStickyHeaderLayout = useCallback(() => {
        const tableEl = bodyTableRef.current;
        if (!tableEl) {
            return;
        }

        const measuredTableWidth = tableEl.scrollWidth;
        if (measuredTableWidth > 0) {
            setHeaderTableWidth(measuredTableWidth);
        }

        const headerCells = Array.from(tableEl.querySelectorAll('thead th'));
        const headerRow = tableEl.querySelector('thead tr');
        const measuredWidths =
            headerCells.length === columns.length
                ? headerCells.map((cell) =>
                      Math.ceil(
                          (cell as HTMLElement).getBoundingClientRect().width,
                      ),
                  )
                : [];

        if (measuredWidths.length === columns.length) {
            setHeaderColumnWidths(measuredWidths);
        } else {
            setHeaderColumnWidths(
                columns.map(
                    (col) => col.minWidth ?? getColumnFallbackMinWidth(col),
                ),
            );
        }

        if (headerRow) {
            setHeaderHeight(
                Math.ceil(
                    (headerRow as HTMLElement).getBoundingClientRect().height,
                ),
            );
        }
    }, [columns]);

    useEffect(() => {
        syncStickyHeaderLayout();
    }, [itemsLength, columns, syncStickyHeaderLayout]);

    useEffect(() => {
        const tableEl = bodyTableRef.current;
        if (!tableEl) {
            return;
        }

        const resizeObserver = new ResizeObserver(() => {
            syncStickyHeaderLayout();
        });
        resizeObserver.observe(tableEl);

        const headerRow = tableEl.querySelector('thead tr');
        if (headerRow) {
            resizeObserver.observe(headerRow);
        }

        return () => {
            resizeObserver.disconnect();
        };
    }, [itemsLength, syncStickyHeaderLayout]);

    useEffect(() => {
        if (scrollContainerRef.current) {
            applyHeaderOffset(scrollContainerRef.current.scrollLeft);
        }
    }, [
        scrollContainerRef,
        headerTableWidth,
        headerColumnWidths.length,
        itemsLength,
        tableMinWidth,
        applyHeaderOffset,
    ]);

    const handleTableScroll = useCallback(
        (event: UIEvent<HTMLDivElement>) => {
            onTableScrolled();
            applyHeaderOffset(event.currentTarget.scrollLeft);
        },
        [onTableScrolled, applyHeaderOffset],
    );

    return {
        bodyTableRef,
        stickyHeaderInnerRef,
        headerColumnWidths,
        headerTableWidth,
        headerHeight,
        handleTableScroll,
    };
};
